import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ShoppingCart, ChevronDown } from 'lucide-react';
import { InstallPWA } from '@/components/InstallPWA';
import { useCart } from '@/context/CartContext';
import { MFS_LOGO } from './logoData';
import { ProductionModal } from './ProductionModal';

const CATEGORY_LINKS = [
  { 
    name: 'WORKSTATIONS', 
    link: '/products?category=Modular Office Workstation',
    subcategories: [
      { name: 'Cluster of 2', link: '/products?category=Modular Office Workstation&subcategory=Cluster of 2' },
      { name: 'Cluster of 4', link: '/products?category=Modular Office Workstation&subcategory=Cluster of 4' },
      { name: 'Cluster of 4 Large', link: '/products?category=Modular Office Workstation&subcategory=Cluster of 4 Large' },
    ]
  },
  { 
    name: 'STORAGE', 
    link: '/products?category=Storage Cabinets',
    subcategories: [
      { name: 'Two Drawer Pedestal', link: '/products?category=Storage Cabinets&subcategory=Two Drawer Pedestal' },
      { name: 'Three Drawer Pedestal', link: '/products?category=Storage Cabinets&subcategory=Three Drawer Pedestal' },
    ]
  },
  { 
    name: 'PRIVACY BOOTHS', 
    link: '/products?category=Privacy Booths',
    subcategories: [
      { name: 'Phone Booth', link: '/products?category=Privacy Booths&subcategory=Phone Booth' },
      { name: 'Meeting Booth', link: '/products?category=Privacy Booths&subcategory=Meeting Booth' },
      { name: 'Meeting Booth Large', link: '/products?category=Privacy Booths&subcategory=Meeting Booth Large' },
    ]
  },
];

const OTHER_LINKS = [
  { name: 'LEGACY', link: '/legacy' },
  { name: 'PROJECTS', link: '/projects' },
  { name: 'ABOUT', link: '/about' },
  { name: 'CONTACT', link: '/contact' },
  { name: 'BLOG', link: '/blog' }
];

function DesktopDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <Link
        to={item.link}
        className="flex items-center gap-1 text-[11px] tracking-[0.08em] font-medium text-black hover:text-[#c5a059] transition-colors whitespace-nowrap"
        data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {item.name}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </Link>
      {open && (
        <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 shadow-lg min-w-[200px] z-50 rounded-sm">
          {item.subcategories.map((sub) => (
            <Link
              key={sub.name}
              to={sub.link}
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-xs tracking-wider text-gray-600 hover:text-[#c5a059] hover:bg-[#F8F6F1] transition-colors font-medium"
            >
              {sub.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileDropdown({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between border-b border-gray-100">
        <Link to={item.link} onClick={onNavigate} className="flex-1 text-sm tracking-[0.15em] font-medium text-gray-700 hover:text-[#c5a059] transition-colors py-3 px-4">
          {item.name}
        </Link>
        <button onClick={() => setOpen(!open)} className="px-4 py-3 text-gray-500 hover:text-[#c5a059] transition-colors">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="bg-gray-50 pl-8">
          {item.subcategories.map((sub) => (
            <Link key={sub.name} to={sub.link} onClick={onNavigate} className="block text-xs tracking-[0.12em] font-medium text-gray-500 hover:text-[#c5a059] transition-colors py-2.5 px-4 border-b border-gray-100">
              {sub.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PremiumHeader({ mobileMenuOpen: externalMobileMenuOpen, setMobileMenuOpen: externalSetMobileMenuOpen }) {
  const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);
  const [showProductionModal, setShowProductionModal] = useState(false);
  const { getItemCount } = useCart();
  const cartCount = getItemCount();
  const location = useLocation();

  // Auto-open production modal if navigated with state
  useEffect(() => {
    if (location.state?.openProduction) {
      setShowProductionModal(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  
  const mobileMenuOpen = externalMobileMenuOpen !== undefined ? externalMobileMenuOpen : internalMobileMenuOpen;
  const setMobileMenuOpen = externalSetMobileMenuOpen || setInternalMobileMenuOpen;
  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 bg-white text-[#1a1a1a] shadow-sm">
      <div className="container mx-auto px-4 sm:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/home-premium" className="flex items-center gap-3 flex-shrink-0">
            <img 
              src={MFS_LOGO}
              alt="Modular Furniture Store Logo"
              style={{ width: '60px', height: '60px', minWidth: '60px', objectFit: 'contain', flexShrink: 0 }}
            />
            <div className="hidden md:block border-l border-gray-200 pl-3">
              <p className="text-[10px] text-gray-500 leading-tight">MFS from the house of</p>
              <p className="text-[11px] text-[#1a1a1a] font-semibold leading-tight">Ezeefit Modular Furniture Pvt. Ltd.</p>
              <p className="text-[10px] text-[#c5a059] font-medium leading-tight">A Legacy Since 2014</p>
            </div>
            <span className="md:hidden text-[9px] text-gray-500 tracking-wider">since 2014</span>
          </Link>

          {/* Nav Links — Desktop */}
          <nav className="hidden lg:flex items-center gap-2 xl:gap-3">
            {CATEGORY_LINKS.map((item) => (
              <DesktopDropdown key={item.name} item={item} />
            ))}
            <button
              onClick={() => setShowProductionModal(true)}
              className="text-[11px] tracking-[0.08em] font-medium text-black hover:text-[#c5a059] transition-colors whitespace-nowrap"
              data-testid="nav-production"
            >
              PRODUCTION
            </button>
            {OTHER_LINKS.map((item) => (
              <Link 
                key={item.name}
                to={item.link}
                className="text-[11px] tracking-[0.08em] font-medium text-black hover:text-[#c5a059] transition-colors whitespace-nowrap"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* CTA - Desktop */}
          <div className="hidden lg:flex items-center gap-3 ml-3 flex-shrink-0">
            <InstallPWA />
            <Link to="/cart" className="relative p-2 text-gray-600 hover:text-[#c5a059] transition-colors" data-testid="cart-button" aria-label="Shopping Cart">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#c5a059] text-[#1a1a1a] text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">{cartCount}</span>
              )}
            </Link>
            <Link to="/bulk-inquiry">
              <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a] rounded-none px-5 py-4 text-[10px] tracking-[0.15em] font-bold transition-all">
                REQUEST QUOTE
              </Button>
            </Link>
            <Link to="/login" className="text-xs tracking-[0.12em] font-medium text-gray-600 hover:text-[#c5a059]">
              LOGIN
            </Link>
          </div>

          {/* CTA - Mobile */}
          <div className="flex lg:hidden items-center gap-2">
            <Link to="/cart" className="relative p-2 text-gray-600 hover:text-[#c5a059] transition-colors" data-testid="cart-button-mobile" aria-label="Shopping Cart">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#c5a059] text-[#1a1a1a] text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">{cartCount}</span>
              )}
            </Link>
            <Link to="/bulk-inquiry">
              <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a] rounded-none px-3 py-2 text-[10px] tracking-[0.1em] font-bold">QUOTE</Button>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-[#1a1a1a] hover:text-[#c5a059] transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white/98 backdrop-blur-md border-t border-gray-200 shadow-lg z-50 max-h-[70vh] overflow-y-auto">
          <nav className="container mx-auto px-4 py-4">
            <div className="flex flex-col">
              {CATEGORY_LINKS.map((item) => (
                <MobileDropdown key={item.name} item={item} onNavigate={closeMobile} />
              ))}
              <button
                onClick={() => { closeMobile(); setShowProductionModal(true); }}
                className="text-left text-sm tracking-[0.15em] font-medium text-gray-700 hover:text-[#c5a059] hover:bg-gray-50 transition-colors py-3 px-4 border-b border-gray-100"
              >
                PRODUCTION
              </button>
              {OTHER_LINKS.map((item) => (
                <Link key={item.name} to={item.link} onClick={closeMobile} className="text-sm tracking-[0.15em] font-medium text-gray-700 hover:text-[#c5a059] hover:bg-gray-50 transition-colors py-3 px-4 border-b border-gray-100">
                  {item.name}
                </Link>
              ))}
              <Link to="/login" onClick={closeMobile} className="text-sm tracking-[0.15em] font-medium text-gray-700 hover:text-[#c5a059] hover:bg-gray-50 transition-colors py-3 px-4">
                LOGIN
              </Link>
            </div>
          </nav>
        </div>
      )}
      {/* Production Modal */}
      <ProductionModal isOpen={showProductionModal} onClose={() => setShowProductionModal(false)} />
    </header>
  );
}
